import { AutomotiveSchedulerStep, IWorkOrderSessionResource } from "../auto-service-scheduler/types/session";
import { IDataLayer } from "JS/types/Analytics";

/**
 * Events used for tracking the current step in the automotive scheduler
 */
export type SchedulerStepEvent =
	| { event: 'scheduler_step_contact' }
	| { event: 'scheduler_step_services_vehicle' }
	| { event: 'scheduler_step_services' }
	| { event: 'scheduler_step_appointment', timeInMinutes: number }
	| { event: 'scheduler_step_payment' }
	| { event: 'scheduler_step_review' };

/**
 * Events used for tracking information regarding appointment type selection and availability
 */
export type SchedulerScheduleEvent =
	| { event: 'scheduler_schedule_dropoff', availableOptions: number }
	| { event: 'scheduler_schedule_timeslot', availableOptions: number };

/**
 * Effectful function that dispatches a scheduler event somewhere
 */
export type SchedulerEventDispatcher = (e: SchedulerStepEvent | SchedulerScheduleEvent) => void;

/**
 * Creates a SchedulerEventDispatcher that logs events to `dataLayer`
 *
 * @param dataLayer IDataLayer to which the event will be pushed
 */
export function createSchedulerEventDispatcher(dataLayer: IDataLayer): SchedulerEventDispatcher {
	return e => dataLayer.push(e);
}

/**
 * Returns a function which logs step change events based on session state to the injected SchedulerEventDispatcher
 */
export function createStepChangeLogger(dispatchSchedulerEvent: SchedulerEventDispatcher) {
	return function logStepChange({
		currentStep,
		nextStep,
		session,
		force = false
	}: {
		/** Step the scheduler is currently on */
		currentStep: AutomotiveSchedulerStep;
		/** Step to which the scheduler will move */
		nextStep: AutomotiveSchedulerStep;
		/** The scheduler session */
		session: IWorkOrderSessionResource;
		/** Log the event even if nextStep is not different from currentStep. Defaults to false. */
		force?: boolean;
	}) {
		if (nextStep === currentStep && !force) {
			return;
		}

		switch (nextStep) {
			case AutomotiveSchedulerStep.ContactInfo: {
				dispatchSchedulerEvent({ event: 'scheduler_step_contact' });
				break;
			}
			case AutomotiveSchedulerStep.Services: {
				dispatchSchedulerEvent({
					event: session.workOrderSummary?.vehicleInfo
						? 'scheduler_step_services'
						: 'scheduler_step_services_vehicle'
				});
				break;
			}
			case AutomotiveSchedulerStep.Appointment: {
				dispatchSchedulerEvent({ event: 'scheduler_step_appointment', timeInMinutes: session.appointmentInfo.estimatedAppointmentDuration });
				break;
			}
			case AutomotiveSchedulerStep.Payment:
				dispatchSchedulerEvent({ event: 'scheduler_step_payment' });
				break;
			case AutomotiveSchedulerStep.Review: {
				dispatchSchedulerEvent({ event: 'scheduler_step_review' });
				break;
			}
		}
	};
}